import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  LeaveEncashmentFormData,
  clearFormDraft,
  readFormDraft,
  submitLeaveEncashment,
} from "./leaveEncashment.service";

const ReviewLeaveEncashment: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const routes = all_routes;
  const formData = ((location.state as any)?.formData || readFormDraft()) as LeaveEncashmentFormData | null;
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const label = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  if (!formData) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <p className="text-muted">{label("no_details_available", "No details available")}</p>
          <Link to={routes.leaveEncashment.create} className="btn btn-danger">{label("employee_leave_encashment", "Employee Leave Encashment")}</Link>
        </div>
      </div>
    );
  }

  const submit = async (isDraft: boolean) => {
    setSubmitting(true);
    try {
      const result = await submitLeaveEncashment(formData, isDraft);
      if (!result.ok) {
        toast.error(result.message || label("something_went_wrong", "Something went wrong"));
        return;
      }
      clearFormDraft();
      setSuccessMessage(
        result.message ||
          (isDraft
            ? label("your_application_has_been_saved_as_draft", "Your application has been saved as draft")
            : label("leave_encashment_submitted_successfully", "Leave encashment submitted successfully"))
      );
      setModalOpen(true);
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || label("something_went_wrong", "Something went wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div>
            <h3 className="page-title mb-1">{label("review_details", "Review Details")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to={routes.leaveEncashment.list}>{label("leave_encashment_overview", "Leave Encashment Overview")}</Link></li>
                <li className="breadcrumb-item active" aria-current="page">{label("review_details", "Review Details")}</li>
              </ol>
            </nav>
          </div>
          <button type="button" className="btn btn-light" onClick={() => navigate(-1)} disabled={submitting}>
            <i className="ti ti-arrow-left me-1"></i>
            {t("back")}
          </button>
        </div>
        <div className="alert alert-light border-start border-danger border-4">
          <div className="fw-bold mb-1">{label("review_your_details", "Review your details")}</div>
          <div>{label("please_review_all_information_before_submitting", "Please review all information before submitting")}</div>
        </div>
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <ReviewItem label={label("leave_type", "Leave Type")} value={formData.leaveType} />
            <ReviewItem label={label("leave_encashment_method", "Leave Encashment Method")} value={formData.method} />
            <ReviewItem label={t("days_to_be_encashed")} value={formData.days} />
            <div className="d-flex justify-content-between align-items-center border rounded p-3 mb-3 bg-light">
              <span className="fw-semibold">{t("Off_Cycle_Payment")}</span>
              <input type="checkbox" checked={false} disabled readOnly aria-label={t("Off_Cycle_Payment")} />
            </div>
            <ReviewItem label={t("note_for_approver")} value={formData.note || t("no_note_added")} />
            <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
              <button type="button" className="btn btn-light" onClick={() => navigate(-1)} disabled={submitting}>{label("preview", "Preview")}</button>
              <button type="button" className="btn btn-outline-danger" onClick={() => submit(true)} disabled={submitting}>{label("save_draft", "Save Draft")}</button>
              <button type="button" className="btn btn-danger" onClick={() => submit(false)} disabled={submitting}>{submitting ? t("syncing") : t("submit")}</button>
            </div>
          </div>
        </div>
      </div>
      {modalOpen && (
        <div className="modal d-block" style={{ background: "rgba(0,0,0,0.45)" }} role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center py-4">
                <i className="ti ti-circle-check fs-1 text-success"></i>
                <p className="mt-3 mb-0">{successMessage}</p>
              </div>
              <div className="modal-footer justify-content-center">
                <button type="button" className="btn btn-danger" onClick={() => navigate(routes.leaveEncashment.list, { replace: true })}>OK</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ReviewItem: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="mb-3">
    <div className="fw-semibold mb-1">{label}</div>
    <div className="border rounded p-3 bg-light">{value || "-"}</div>
  </div>
);

export default ReviewLeaveEncashment;
